import * as React from 'react';

import EffortTextV2, { getEffortTextV2 } from 'bundles/course-frame/components/course-outline/EffortTextV2';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getIsReviewPartForSplitPeer } from 'bundles/learner-progress/utils/Item';
import { truncToPlace } from 'bundles/primitive-formatting/utils/numberUtils';

import _t from 'i18n!nls/course-frame';

/**
 * Logic to render one thing after the item type in the new Course Frame navigation.
 * It can be the time estimate, or a grading status, or a grade, or in some situations, nothing.
 * @returns string, intended for use for aria-label etc. See ItemNavigationLabel to render a React element.
 */
export const getItemNavigationLabel = (computedItem: Item): string | undefined => {
  if (getIsReviewPartForSplitPeer(computedItem)) {
    return undefined;
  }

  if (computedItem.isSubmitted && !computedItem.isGraded) {
    return _t('Results pending');
  }

  if (computedItem.isSubmitted && computedItem.isGraded) {
    const grade = computedItem.itemGrade.get('overallOutcome').grade;
    const gradePercentNumber = truncToPlace(grade * 100, 2);
    return _t('Grade: #{gradePercentNumber}%', { gradePercentNumber });
  }

  return getEffortTextV2(computedItem);
};

type Props = {
  computedItem: Item;
};

/**
 * Logic to render one thing after the item type in the new Course Frame navigation.
 * It can be the time estimate, or a grading status, or a grade, or in some situations, nothing.
 * @returns React element. For a string, see getItemNavigationLabel.
 */
export const ItemNavigationLabel = ({ computedItem, ...rest }: Props) => {
  if (getIsReviewPartForSplitPeer(computedItem)) {
    return null;
  }

  if (computedItem.isSubmitted && !computedItem.isGraded) {
    return <span {...rest}>{_t('Results pending')}</span>;
  }

  if (computedItem.isGraded) {
    const grade = computedItem.itemGrade.get('overallOutcome').grade;
    const gradePercentNumber = truncToPlace(grade * 100, 2);
    return <span {...rest}>{_t('Grade: #{gradePercentNumber}%', { gradePercentNumber })}</span>;
  }

  return <EffortTextV2 computedItem={computedItem} {...rest} />;
};

export default ItemNavigationLabel;
